import { v4 as uuidv4 } from 'uuid';
import CryptoJS from 'crypto-js/core';
import { encrypt, decrypt } from '@/services/SecurityService';
import { EMPTY_GUID } from '@/constants/app';
import {
  getAllNoteRows,
  getNoteRow,
  putNoteRow,
  deleteNoteRow,
  NoteRow
} from '@/storage/NotesDb';
import { deleteMedia } from '@/storage/DataStorage';

export interface AudioResourceMetadata {
  id: string;
  title: string;
}

export interface NoteMetadata {
  note_title: string;
  lessonId: string;       // GUID or EMPTY_GUID
  lessonTitle: string;
  lessonIconId: string;   // GUID or EMPTY_GUID
  lessonSetId: string;    // GUID or EMPTY_GUID
  date_created: string;   // ISO
  date_modified: string;  // ISO
  audioResourceIds?: string[]; // IDs in media store for attached audio (legacy)
  audioResources?: AudioResourceMetadata[]; // Audio clips with titles
}

export interface Note {
  id: string;             // UUID (EMPTY_GUID until first save)
  note_metadata: NoteMetadata;
  note_body: string;
}

export function createNewNote(initial?: Partial<Note>): Note {
  const nowIso = new Date().toISOString();
  return {
    id: EMPTY_GUID,
    note_metadata: {
      note_title: '',
      date_created: nowIso,
      date_modified: nowIso,
      lessonId: EMPTY_GUID,
      lessonTitle: '',
      lessonIconId: EMPTY_GUID,
      lessonSetId: EMPTY_GUID,
      audioResourceIds: [],
      audioResources: [],
      ...(initial?.note_metadata || {})
    },
    note_body: '',
    ...initial,
  };
}

function encryptBody(body: string): string {
  return encrypt(body || '').toString();
}

function decryptBody(content: string): string {
  return decrypt(content).toString(CryptoJS.enc.Utf8);
}

function decryptMetadata(metadata: string): NoteMetadata {
  const json = decrypt(metadata).toString(CryptoJS.enc.Utf8);
  return JSON.parse(json) as NoteMetadata;
}

function fromRow(row: NoteRow): Note {
  // New format: body in content, metadata in row.metadata
  if (row.metadata) {
    const body = decryptBody(row.content);
    const meta = decryptMetadata(row.metadata);
    return { id: row.id, note_metadata: meta, note_body: body };
  }
  // Backward compatibility: content contains encrypted Note JSON
  const json = decrypt(row.content).toString(CryptoJS.enc.Utf8);
  try {
    const legacy = JSON.parse(json) as any;
    if (legacy && legacy.note_metadata && typeof legacy.note_body === 'string') {
      return legacy as Note;
    }
  } catch {
    // fallthrough
  }
  // Fallback: treat as plain body with empty metadata
  const nowIso = new Date().toISOString();
  return {
    id: row.id,
    note_metadata: {
      note_title: '',
      date_created: nowIso,
      date_modified: nowIso,
      lessonId: EMPTY_GUID,
      lessonTitle: '',
      lessonIconId: EMPTY_GUID,
      lessonSetId: EMPTY_GUID,
      audioResourceIds: [],
      audioResources: [],
    },
    note_body: json || ''
  };
}

export async function getAllNotes(): Promise<Note[]> {
  const rows = await getAllNoteRows();
  return rows.map(r => fromRow(r));
}

export async function getNote(id: string): Promise<Note | undefined> {
  const row = await getNoteRow(id);
  return row ? fromRow(row) : undefined;
}

export function deriveAutoTitle(body: string): string {
  const text = (body || '').trim();
  if (!text) return '';
  const firstSentenceBreak = Math.max(
    text.indexOf('\n'),
    text.indexOf('. ')
  );
  const sentence = firstSentenceBreak > 0 ? text.substring(0, firstSentenceBreak) : text;
  return sentence.substring(0, 24);
}

export function ensureTitle(note: Note): Note {
  if (!note.note_metadata.note_title || note.note_metadata.note_title.trim().length === 0) {
    note.note_metadata.note_title = deriveAutoTitle(note.note_body);
  }
  return note;
}

export async function saveNote(note: Note): Promise<Note> {
  const nowIso = new Date().toISOString();
  let isNew = note.id === EMPTY_GUID;
  let id = note.id;
  // Enforce one-per-lesson: if new and lessonId provided, reuse existing id
  if (isNew && note.note_metadata?.lessonId && note.note_metadata.lessonId !== EMPTY_GUID) {
    const existing = await findByLessonId(note.note_metadata.lessonId);
    if (existing) {
      id = existing.id;
      isNew = false;
    }
  }
  if (isNew) {
    id = uuidv4();
  }
  const toSave: Note = ensureTitle({ ...note, id });
  toSave.note_metadata.date_created = isNew ? nowIso : toSave.note_metadata.date_created;
  toSave.note_metadata.date_modified = nowIso;
  const row: NoteRow = { id: toSave.id, content: encryptBody(toSave.note_body), metadata: encrypt(JSON.stringify(toSave.note_metadata)).toString() };
  await putNoteRow(row);
  return toSave;
}

export async function updateNote(note: Note): Promise<Note> {
  // Alias to saveNote; ensures modified date is updated
  return saveNote(note);
}

export async function deleteNote(id: string): Promise<void> {
  // Get the note first to access its audio resources
  const note = await getNote(id);
  if (note) {
    // Delete all associated audio clips from media store
    const audioResources = note.note_metadata.audioResources || [];
    const legacyIds = note.note_metadata.audioResourceIds || [];
    
    // Collect all audio resource IDs (from new format and legacy)
    const allAudioIds = new Set<string>();
    audioResources.forEach(r => allAudioIds.add(r.id));
    legacyIds.forEach(id => allAudioIds.add(id));
    
    // Delete each audio clip from media store
    for (const mediaId of allAudioIds) {
      try {
        await deleteMedia(mediaId);
      } catch (e) {
        // Continue deleting other clips even if one fails
        console.error(`Failed to delete media ${mediaId}:`, e);
      }
    }
  }
  
  // Delete the note
  await deleteNoteRow(id);
}

export async function findByLessonId(lessonId: string): Promise<Note | undefined> {
  if (!lessonId) return undefined;
  const rows = await getAllNoteRows();
  for (const r of rows) {
    const n = fromRow(r);
    if (n.note_metadata?.lessonId?.toLowerCase() === lessonId.toLowerCase()) {
      return n;
    }
  }
  return undefined;
}

export async function searchNotes(query: string): Promise<Note[]> {
  const q = (query || '').trim().toLowerCase();
  if (!q || q === ''){
    return getAllNotes();
  } 
  const rows = await getAllNoteRows();
  return rows
    .map(r => fromRow(r))
    .filter(n =>
      (n.note_metadata?.note_title || '').toLowerCase().includes(q) ||
      (n.note_body || '').toLowerCase().includes(q)
    );
}

export function toShareText(note: Note): string {
  const title = (note.note_metadata?.note_title || '').trim();
  const body = (note.note_body || '').trim();
  return `${title}\n\n${body}`.trim();
}


